import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DomainValueType } from '@app/core/enums/domain-values.enum';
import { EventMedium } from '@app/core/enums/event-medium.enum';
import { LeadStateEnum } from '@app/core/enums/lead-state.enum';
import { getOpportunityPurchaseStateOptions, OpportunityPurchaseStateEnum } from '@app/core/enums/opportunity-purchase-state.enum';
import { OpportunitySaleStateEnum } from '@app/core/enums/opportunity-sale-state.enum';
import { ProjectType } from '@app/core/enums/project-type.enum';
import { IDomainValue } from '@app/core/interfaces/domain-value.interface';
import { DomainValuesSharedService } from '@app/core/services/domain-values.shared.service';
import { SharedLibModule } from '@app/core/shared-lib/shared-lib.module';
import { MessageService, SelectItem } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, Subscription } from 'rxjs';
import { Slot } from './future-event-form';
import { FutureEventFacade } from './future-event.facade';
import { IDateRange } from '@app/core/interfaces/date-range.interface';
import { ProjectsService } from '@app/client/api/projects.service';
import { AuthServices } from '@app/core/services/auth.services';
import {
  BACKOFFICE_SELF_ASSIGN_EVENT_TYPES,
  BACKOFFICE_SELF_ASSIGN_VIRTUAL_MEDIUM_EVENT_TYPES,
  hasBackofficeSelfAssignRole,
  selfAssignSlotAccountLabel,
} from '@app/core/constants/backoffice-self-assign.constants';

@Component({
  templateUrl: `future-event-creation-modal.component.html`,
  standalone: true,
  providers: [FutureEventFacade],
  imports: [SharedLibModule],
})
export class FutureEventCreationModalComponent implements OnInit {
  dialog = inject(DynamicDialogRef<FutureEventCreationModalComponent>);
  dialogConfig = inject(DynamicDialogConfig);
  messageService = inject(MessageService);
  domainValuesService = inject(DomainValuesSharedService);
  router = inject(Router);
  futureEventFacade = inject(FutureEventFacade);
  projectsService = inject(ProjectsService);
  authService = inject(AuthServices);

  type: 'vendita' | 'acquisto' = 'vendita';
  projectId!: string;
  canAssignToSelf = false;
  fb: FormBuilder = new UntypedFormBuilder();
  states = getOpportunityPurchaseStateOptions();
  domainValuesList$!: Observable<IDomainValue[]>;
  usersList!: SelectItem[];
  slots!: Slot[];

  eventMediums!: IDomainValue[];
  eventTypes!: IDomainValue[];
  filteredEventTypes!: IDomainValue[];
  filteredEventMediums!: IDomainValue[];
  opportunityState?: number;
  leadState!: number;
  isRenting: boolean = false;

  checkForAddProperty = false;
  checkForSelectProperty = false;
  mustAddProperty = false;
  mustSelectProperty = false;
  selectedProperty!: any;
  actionSub!: Subscription;
  leadType!: ProjectType;
  propertyProjectList!: SelectItem[];

  request!: any;
  formEvent = this.fb.group({
    eventRange: new FormControl<IDateRange | undefined>(undefined, [Validators.required]),
    slot: new FormControl(undefined, [Validators.required]),
    medium: new FormControl<EventMedium | undefined>(undefined, { nonNullable: true, validators: [Validators.required] }),
    note: new FormControl('', [Validators.required, Validators.minLength(5)]),
    connectionInfo: new FormControl('', []),
    project: new FormControl(this.projectId, [Validators.required]),
    address: new FormControl(undefined, []),
    connectedProject: new FormControl(undefined, [this.mustSelectProperty ? Validators.required : Validators.nullValidator]),
    eventType: new FormControl(0, [Validators.required]),
    notVisible: new FormControl(false, [Validators.required]),
    assignToSelf: new FormControl(false, [Validators.required]),
  });

  ngOnInit(): void {
    this.projectId = this.dialogConfig?.data?.projectId;

    this.authService.user().subscribe((user) => {
      this.canAssignToSelf = hasBackofficeSelfAssignRole(user.roles);
    });

    this.domainValuesList$ = this.domainValuesService.getDomainValues();
    this.domainValuesList$.subscribe((res) => {
      this.eventMediums = res.filter((d: IDomainValue) => d.type === DomainValueType.EVENT_MEDIUM);
      this.eventTypes = res.filter((d: IDomainValue) => d.type === DomainValueType.EVENT_TYPE);
    });

    this.states = this.states.filter(
      (state) =>
        state.value === OpportunityPurchaseStateEnum.PRESENTATO ||
        state.value === OpportunityPurchaseStateEnum.INTERESSANTE ||
        state.value === OpportunityPurchaseStateEnum.STANDBY,
    );

    // Determina il tipo di progetto (vendita/acquisto) prima di caricare i dati
    this.projectsService.findOne(this.projectId).subscribe((project) => {
      this.type = project.type as 'vendita' | 'acquisto';

      this.futureEventFacade.getData(this.projectId, this.type).subscribe((res) => {
        this.formEvent.patchValue({ project: this.projectId });
        this.leadType = res.type;
        this.opportunityState = res.opportunityState;
        this.leadState = res.leadState;
        this.isRenting = res.isRenting;
        this.propertyProjectList = res.propertyProject;
        // Aggiorna gli stati in base al tipo di progetto (vendita/affitto)
        this.states = getOpportunityPurchaseStateOptions(this.isRenting);
        this.filterEventTypes();
      });
    });
  }

  async save() {
    if (
      (this.formEvent.valid && this.leadType === ProjectType.VENDITA) ||
      (this.formEvent.valid &&
        this.leadType === ProjectType.ACQUISTO &&
        !this.mustAddProperty &&
        this.mustSelectProperty &&
        this.formEvent.get('connectedProject')!.value) ||
      (this.formEvent.valid && this.leadType === ProjectType.ACQUISTO && this.eventMedium === EventMedium.Virtuale) ||
      this.eventMedium === EventMedium.Free ||
      (this.formEvent.valid && this.leadType === ProjectType.ACQUISTO && this.eventMedium === EventMedium.Fisico) /* &&
        !this.checkForAddProperty &&
        !this.checkForSelectProperty */
    ) {
      this.request = this.getFormValues();

      this.futureEventFacade.createFutureEvent(this.request, this.duration).subscribe(() => {
        this.close();
      });
    }
  }

  checkEventAvailability() {
    this.formEvent.get('slot')!.reset();
    const form = this.getFormValues();
    if (form.eventRange) {
      this.futureEventFacade
        .getAvailabilitySlots(
          this.projectId,
          new Date(form.eventRange.start),
          new Date(form.eventRange.end),
          this.duration,
          this.eventMedium,
          this.assignToSelf,
        )
        .subscribe((res) => {
          this.slots = [];
          const now = new Date();

          res.forEach((agent) => {
            agent.slots.forEach((slot) => {
              const slotDate = new Date(slot);
              if (!this.isFutureSlot(slotDate, now)) {
                return;
              }

              this.slots.push({
                account: selfAssignSlotAccountLabel(this.assignToSelf, agent.email),
                slot: slotDate,
                userId: agent.userId,
              });
            });
          });
        });
    }
  }

  onAssignToSelfChange() {
    this.formEvent.get('slot')!.reset();
    this.formEvent.get('eventType')!.reset();
    this.formEvent.get('medium')!.reset();
    this.formEvent.get('address')!.reset();
    this.formEvent.get('connectedProject')!.reset();
    this.mustAddProperty = false;
    this.mustSelectProperty = false;
    this.filterEventTypes();

    if (this.formEvent.get('eventRange')!.value) {
      this.checkEventAvailability();
    }
  }

  getFormValues() {
    const form = this.formEvent.value;
    form.notVisible = this.eventMedium === EventMedium.Fisico ? true : false;
    return form;
  }

  /**
   * Verifica se lo slot è nel futuro (oggi dopo adesso, oppure un giorno successivo).
   */
  private isFutureSlot(slotDate: Date, now: Date): boolean {
    if (this.isSameDay(slotDate, now)) {
      return slotDate > now;
    }

    return slotDate > now;
  }

  /**
   * Verifica se due date sono nello stesso giorno
   */
  private isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  close() {
    this.dialog.close();
  }

  get eventMedium() {
    return this.formEvent.get('medium')!.value;
  }

  get eventType() {
    return this.formEvent.get('eventType')!.value;
  }

  get assignToSelf() {
    return this.formEvent.get('assignToSelf')!.value ?? false;
  }

  get duration() {
    let duration = 1.5;
    if (this.eventMedium === EventMedium.Fisico) {
      duration = 1;
    }
    if (this.eventMedium === EventMedium.Virtuale) {
      //sms/wa
      duration = 0.5;
    }
    if (this.eventType === 3) {
      //Call
      duration = 0.25;
    }
    if (this.eventType === 2) {
      //Visita
      duration = 0.5;
    }

    if (this.eventType === 12 || this.eventType === 10) {
      //Atto o Preliminare
      duration = 2;
    }
    if (this.eventType === 6) {
      //SMS/WA
      duration = 0.0834;
    }

    if (this.eventMedium === EventMedium.Free) {
      duration = 0.25;
    }

    return duration;
  }

  selectEventMedium() {
    if (this.eventType) {
      if (this.assignToSelf && BACKOFFICE_SELF_ASSIGN_VIRTUAL_MEDIUM_EVENT_TYPES.includes(this.eventType)) {
        this.formEvent.get('medium')!.setValue(EventMedium.Virtuale);
      } else if ([2, 7, 8, 9, 10, 11, 12, 13, 14, 15].includes(this.eventType)) {
        this.formEvent.get('medium')!.setValue(EventMedium.Fisico);
      } else if ([3, 4].includes(this.eventType)) {
        this.formEvent.get('medium')!.setValue(EventMedium.Virtuale);
      } else if ([16, 5, 6].includes(this.eventType)) {
        this.formEvent.get('medium')!.setValue(EventMedium.Free);
      }
    }
  }

  filterEventTypes() {
    this.checkForAddProperty = false;
    this.checkForSelectProperty = false;
    this.filteredEventTypes = this.eventTypes;
    /* if (this.eventMedium === EventMedium.Fisico) {
      this.filteredEventTypes = this.eventTypes.filter((e) =>
        [2, 7, 8, 9, 10, 11, 12, 13, 14, 15].includes(e.code)
      );
    } else if (this.eventMedium === EventMedium.Virtuale) {
      this.filteredEventTypes = this.eventTypes.filter((e) => [3, 4, 5, 6].includes(e.code));
    } else {
      this.filteredEventTypes = this.eventTypes.filter((e) => [16].includes(e.code));
    }  */
    let eventTypeFilter: number[] = [];
    if (this.opportunityState !== undefined) {
      this.filteredEventMediums = this.eventMediums;
              if (this.leadType === ProjectType.VENDITA) {
          switch (this.opportunityState) {
            case OpportunitySaleStateEnum.APPUNTAMENTO_DA_FISSARE:
              eventTypeFilter = [3, 4, 5, 6, 13, 16];
              break;
            case OpportunitySaleStateEnum.PRIMO_APPUNTAMENTO:
              eventTypeFilter = [3, 4, 5, 6, 13, 16];
              break;
            case OpportunitySaleStateEnum.DA_VALUTARE:
              eventTypeFilter = [3, 4, 5, 6, 9, 16];
              break;
            case OpportunitySaleStateEnum.VALUTAZIONE_CONSEGNATA:
              eventTypeFilter = [3, 4, 5, 6, 9, 16];
              break;
            case OpportunitySaleStateEnum.PROPOSTA_TRATTATIVA:
              eventTypeFilter = [3, 4, 5, 6, 9, 10, 15, 16];
              break;
            case OpportunitySaleStateEnum.INCARICO:
              eventTypeFilter = [3, 4, 5, 6, 9, 14, 16];
              break;
            case OpportunitySaleStateEnum.STANDBY:
              eventTypeFilter = [3, 4, 5, 6, 9, 16];
              break;
            case OpportunitySaleStateEnum.VENDUTO:
              eventTypeFilter = [3, 4, 5, 6, 9, 11, 12, 16];
              break;
            case OpportunitySaleStateEnum.KO:
            case OpportunitySaleStateEnum.KO_SOPRALLUOGO:
            case OpportunitySaleStateEnum.KO_GESTIONE:
            case OpportunitySaleStateEnum.KO_INCARICO_SCADUTO:
              eventTypeFilter = [3, 5, 6, 9, 16];
        }
      } else {
                  switch (this.opportunityState) {
            case OpportunityPurchaseStateEnum.STANDBY:
            case OpportunityPurchaseStateEnum.INTERESSANTE:
            case OpportunityPurchaseStateEnum.PRESENTATO:
              eventTypeFilter = [2, 3, 4, 5, 6, 7, 8, 9, 16];
              this.checkForAddProperty = this.eventMedium === EventMedium.Fisico;
              break;
            case OpportunityPurchaseStateEnum.PRIMA_VISITA:
            case OpportunityPurchaseStateEnum.INTERESSATO:
              eventTypeFilter = [2, 3, 4, 5, 6, 7, 8, 9, 16];
              this.checkForAddProperty = this.eventMedium === EventMedium.Fisico;
              this.checkForSelectProperty = this.eventMedium === EventMedium.Fisico;
              break;
            case OpportunityPurchaseStateEnum.PROPOSTA_TRATTATIVA:
              eventTypeFilter = [3, 4, 5, 6, 7, 8, 9, 10, 11, 16];
              this.checkForAddProperty = this.eventMedium === EventMedium.Fisico;
              this.checkForSelectProperty = this.eventMedium === EventMedium.Fisico;
              break;
            case OpportunityPurchaseStateEnum.ACQUISTATO:
              eventTypeFilter = [3, 4, 5, 6, 11, 12, 16];
              break;
            case OpportunityPurchaseStateEnum.KO_GIA_VENDUTO:
            case OpportunityPurchaseStateEnum.KO_NON_COMPRA_PIU:
            case OpportunityPurchaseStateEnum.KO_GIA_COMPRATO:
            case OpportunityPurchaseStateEnum.KO_RECESSO:
              eventTypeFilter = [3, 5, 6, 16];
        }
      }
    } else {
      switch (this.leadState) {
        case LeadStateEnum.DA_CHIAMARE:
        case LeadStateEnum.KO_GENERICO:
        case LeadStateEnum.KO_NON_INTERESSATO:
        case LeadStateEnum.KO_NON_REPERIBILE:
        case LeadStateEnum.KO_NUMERO_ERRATO:
        case LeadStateEnum.KO_GIA_IN_VENDITA:
        case LeadStateEnum.DA_VERIFICARE:
        case LeadStateEnum.DA_RICONTATTARE:
          eventTypeFilter = [3, 5, 6, 16];
          break;
        case LeadStateEnum.OPPORTUNITA_CREATA:
        case LeadStateEnum.IN_LAVORAZIONE:
          eventTypeFilter = [3, 4, 5, 6, 16];
          break;
      }
      this.filteredEventMediums = this.eventMediums.filter((e) => e.code === EventMedium.Virtuale || e.code === EventMedium.Free);
    }
    this.filteredEventTypes = this.filteredEventTypes.filter((e) => eventTypeFilter.includes(e.code));

    if (this.assignToSelf) {
      this.filteredEventTypes = this.filteredEventTypes.filter((e) =>
        BACKOFFICE_SELF_ASSIGN_EVENT_TYPES.includes(e.code),
      );
    }
  }

  checkOrAddProperty() {}

  checkSelectProperty() {
    this.mustSelectProperty = !this.mustAddProperty && this.checkForSelectProperty;
  }

  goToOpportunity() {
    this.router.navigate(['/sale/leads/working-unit'], {
      queryParams: { id: this.projectId },
    });
    this.close();
  }
}
